import { NgModule } from '@angular/core';
import { ApolloModule, APOLLO_OPTIONS } from 'apollo-angular';
import {
  ApolloClientOptions,
  ApolloLink,
  InMemoryCache,
  split,
} from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { HttpLink } from 'apollo-angular/http';
import * as Sentry from '@sentry/angular-ivy';
import { filter, take } from 'rxjs';
import { Observable } from 'zen-observable-ts';

import { SocketService } from '@core/services/socket.service';
import { environment } from '../environments/environment';

const uri = environment.api + '/gql';

export function createApollo(
  httpLink: HttpLink,
  socketService: SocketService
): ApolloClientOptions<{}> {
  const authLink = new ApolloLink((operation, forward) => {
    // Get token from context or fallback to localStorage
    const token = operation.getContext()['authorization'];

    if (token) {
      operation.setContext({
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return forward(operation);
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    const authStorage = localStorage.getItem('auth');
    const auth = authStorage ? JSON.parse(authStorage) : null;
    const userData = auth?.user;
    const user = userData
      ? {
          id: userData?.id || '-',
          email: userData?.email || '-',
          surname: userData?.surname || '-',
        }
      : undefined;

    if (graphQLErrors) {
      graphQLErrors.forEach((err) => {
        const { message, locations, path } = err;
        const formattedError = `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`;
        Sentry.captureMessage(formattedError, { user });
      });
    }

    if (networkError) {
      Sentry.captureException(networkError, { user });
      console.log(`[Network error]:`, networkError);
    }
  });

  const http = httpLink.create({ uri });

  // 🔑 Dynamic WS link: waits for SocketService.ready$, then forwards
  const dynamicWsLink = new ApolloLink((operation) => {
    return new Observable((observer) => {
      let innerSub: any;

      const readySub = socketService.ready$
        .pipe(filter(Boolean), take(1))
        .subscribe(() => {
          const liveWsLink = socketService.getLink();
          if (!liveWsLink || !liveWsLink.request) {
            observer.complete();
            return;
          }

          const wsObs = liveWsLink.request(operation);
          if (!wsObs) {
            observer.complete();
            return;
          }

          innerSub = wsObs.subscribe({
            next: (v) => observer.next(v),
            error: (e) => observer.error(e),
            complete: () => observer.complete(),
          });
        });

      // Teardown
      return () => {
        try {
          readySub.unsubscribe();
        } catch {}
        try {
          innerSub?.unsubscribe?.();
        } catch {}
      };
    });
  });

  const splitLink = split(
    ({ query }) => {
      const def = getMainDefinition(query);
      return (
        def.kind === 'OperationDefinition' && def.operation === 'subscription'
      );
    },
    dynamicWsLink,
    http
  );

  const link = ApolloLink.from([errorLink, authLink, splitLink]);
  const cache = new InMemoryCache();

  return {
    link,
    cache,
    defaultOptions: {
      query: { fetchPolicy: 'no-cache' },
      mutate: { fetchPolicy: 'no-cache' },
    },
  };
}

@NgModule({
  exports: [ApolloModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, SocketService],
    },
  ],
})
export class GraphQLModule {}
